import { useCallback, useEffect, useRef, useState } from "react";
import Button from "../atoms/button";
import Loading from "../atoms/loading";
import {
    listChannels,
    createChannel,
    getChannelStatus,
    getChannelQr,
    logoutChannel,
} from "./functions";

const STATE_LABELS = {
    open: "Conectado",
    close: "Desconectado",
    connecting: "Conectando…",
};

const stateColorClass = (state) => {
    if (state === "open") return "text-green-400";
    if (state === "connecting") return "text-amber-300";
    return "text-red-400";
};

const BotOpsChannelPanel = ({ token, onToastGood, onToastBad, onChannelSummary, isActive }) => {
    const [loading, setLoading] = useState(true);
    const [instances, setInstances] = useState([]);
    const [qrByName, setQrByName] = useState({});
    const [newChannelName, setNewChannelName] = useState("");
    const [creating, setCreating] = useState(false);
    const [busyName, setBusyName] = useState(null);
    const instancesRef = useRef(instances);
    const qrByNameRef = useRef(qrByName);

    useEffect(() => {
        instancesRef.current = instances;
    }, [instances]);

    useEffect(() => {
        qrByNameRef.current = qrByName;
    }, [qrByName]);

    const loadChannels = useCallback(async () => {
        setLoading(true);
        const data = await listChannels(token);
        setLoading(false);

        if (data?.type === "error") {
            onToastBad(data.message || "Erro ao carregar canais");
            return;
        }

        const list = Array.isArray(data?.instances) ? data.instances : [];
        setInstances(list);
    }, [token, onToastBad]);

    useEffect(() => {
        loadChannels();
    }, [loadChannels]);

    useEffect(() => {
        const hasClosed = instances.some((i) => i.state === "close");
        if (typeof onChannelSummary === "function") {
            onChannelSummary({ hasClosed });
        }
    }, [instances, onChannelSummary]);

    useEffect(() => {
        if (!isActive) return undefined;

        const poll = async () => {
            const currentInstances = instancesRef.current;
            const currentQr = qrByNameRef.current;
            const namesToPoll = currentInstances
                .filter((i) => currentQr[i.name] && i.state !== "open")
                .map((i) => i.name);

            for (const name of namesToPoll) {
                const qrData = await getChannelQr(token, name);
                if (qrData?.type !== "error") {
                    if (qrData?.qrcode_base64 || qrData?.pairing_code) {
                        setQrByName((prev) => ({
                            ...prev,
                            [name]: {
                                qrcode_base64: qrData.qrcode_base64,
                                pairing_code: qrData.pairing_code,
                            },
                        }));
                    }

                    if (qrData?.state) {
                        setInstances((prev) =>
                            prev.map((i) =>
                                i.name === name ? { ...i, state: qrData.state } : i
                            )
                        );
                    } else {
                        const statusData = await getChannelStatus(token, name);
                        if (statusData?.type !== "error" && statusData?.state) {
                            setInstances((prev) =>
                                prev.map((i) =>
                                    i.name === name ? { ...i, state: statusData.state } : i
                                )
                            );
                        }
                    }
                }
            }
        };

        const interval = setInterval(poll, 20000);
        return () => clearInterval(interval);
    }, [isActive, token]);

    const handleRefresh = async (name) => {
        setBusyName(name);
        const data = await getChannelStatus(token, name);
        setBusyName(null);

        if (data?.type === "error") {
            onToastBad(data.message || "Erro ao atualizar status");
            return;
        }

        if (data?.state) {
            setInstances((prev) =>
                prev.map((i) => (i.name === name ? { ...i, state: data.state } : i))
            );
        }
        onToastGood("Status atualizado");
    };

    const handleShowQr = async (name) => {
        setBusyName(name);
        const data = await getChannelQr(token, name);
        setBusyName(null);

        if (data?.type === "error") {
            onToastBad(data.message || "Erro ao obter QR");
            return;
        }

        if (data?.qrcode_base64) {
            setQrByName((prev) => ({
                ...prev,
                [name]: {
                    qrcode_base64: data.qrcode_base64,
                    pairing_code: data.pairing_code,
                },
            }));
        } else if (data?.pairing_code) {
            setQrByName((prev) => ({
                ...prev,
                [name]: { pairing_code: data.pairing_code },
            }));
        } else {
            onToastBad("QR indisponível no momento");
        }

        if (data?.state) {
            setInstances((prev) =>
                prev.map((i) => (i.name === name ? { ...i, state: data.state } : i))
            );
        }
    };

    const handleLogout = async (name) => {
        const confirmed = window.confirm("Desconectar WhatsApp desta linha?");
        if (!confirmed) return;

        setBusyName(name);
        const data = await logoutChannel(token, name);
        setBusyName(null);

        if (data?.type === "error") {
            onToastBad(data.message || "Erro ao desconectar");
            return;
        }

        setQrByName((prev) => {
            const next = { ...prev };
            delete next[name];
            return next;
        });
        onToastGood("Sessão desconectada");
        await loadChannels();
    };

    const handleCreate = async () => {
        const name = newChannelName.trim();
        if (!name) {
            onToastBad("Informe um nome para a linha");
            return;
        }

        setCreating(true);
        const data = await createChannel(token, name);
        setCreating(false);

        if (data?.type === "error") {
            onToastBad(data.message || "Erro ao criar canal");
            return;
        }

        setNewChannelName("");
        onToastGood("Canal criado");
        await loadChannels();
    };

    const hasClosed = instances.some((i) => i.state === "close");

    if (loading) {
        return <Loading classes="m-auto mt-4" />;
    }

    return (
        <div className="space-y-4">
            {hasClosed ? (
                <div className="rounded-lg border border-red-500/50 bg-red-950/30 p-3 text-sm text-red-200">
                    Uma ou mais linhas WhatsApp estão desconectadas. Escaneie o QR na Evolution
                    para restabelecer o atendimento.
                </div>
            ) : null}

            {instances.length === 0 ? (
                <p className="text-sm text-gray-300">Nenhuma linha WhatsApp cadastrada.</p>
            ) : (
                instances.map((instance) => {
                    const label = STATE_LABELS[instance.state] || instance.state || "—";
                    const busy = busyName === instance.name;

                    return (
                        <div
                            key={instance.name}
                            className="rounded-lg border border-[#b47c06]/60 bg-gray-900/60 p-4 space-y-3"
                        >
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <div>
                                    <p className="font-medium">{instance.name}</p>
                                    <p className={`text-sm ${stateColorClass(instance.state)}`}>
                                        {label}
                                    </p>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    <Button
                                        click={() => handleRefresh(instance.name)}
                                        disabled={busy}
                                        classes="bg-gray-600 hover:bg-gray-500 px-3 py-1 font-medium text-white"
                                    >
                                        Atualizar
                                    </Button>
                                    <Button
                                        click={() => handleShowQr(instance.name)}
                                        disabled={busy}
                                        classes="bg-blue-300 hover:bg-blue-400 px-3 py-1"
                                    >
                                        Mostrar QR
                                    </Button>
                                    <Button
                                        click={() => handleLogout(instance.name)}
                                        disabled={busy}
                                        classes="bg-red-500 hover:bg-red-600 px-3 py-1"
                                    >
                                        Desconectar
                                    </Button>
                                </div>
                            </div>

                            {qrByName[instance.name] ? (
                                <div className="bg-white rounded p-2 inline-block">
                                    {qrByName[instance.name].qrcode_base64 ? (
                                        <img
                                            src={qrByName[instance.name].qrcode_base64}
                                            alt={`QR WhatsApp ${instance.name}`}
                                            className="max-w-[220px] h-auto"
                                        />
                                    ) : qrByName[instance.name].pairing_code ? (
                                        <p className="text-black text-lg font-mono tracking-widest">
                                            {qrByName[instance.name].pairing_code}
                                        </p>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>
                    );
                })
            )}

            <div className="rounded-lg border border-dashed border-[#b47c06]/40 bg-gray-900/40 p-4">
                <p className="text-sm text-gray-300 mb-2">Nova linha WhatsApp</p>
                <div className="flex flex-wrap gap-2 items-center">
                    <input
                        type="text"
                        value={newChannelName}
                        onChange={(e) => setNewChannelName(e.target.value)}
                        placeholder="nome-da-instancia"
                        className="flex-1 min-w-[200px] text-black p-2 rounded"
                    />
                    <Button
                        click={handleCreate}
                        disabled={creating}
                        classes="bg-[#d59106] hover:bg-[#b47c06] px-4 py-2"
                    >
                        {creating ? "Criando…" : "Criar"}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default BotOpsChannelPanel;
